import { IAggregatedPosition, IPerpAggregatedPosition } from "../interfaces/positions";
import { retrieveAssetByInstrumentName } from "./strings";

interface IMarketLike {
  underlying_asset: string;
  mark_price: string | number;
}

const sortPerpPositions = (positions: IPerpAggregatedPosition[]) =>
  positions.sort((a, b) => {
    if (a.contracts > 0 && b.contracts <= 0) return -1;
    if (a.contracts < 0 && b.contracts > 0) return 1;
    if (a.contracts === 0 && b.contracts !== 0) return 1;
    if (b.contracts === 0 && a.contracts !== 0) return -1;
    return 0;
  });

export const computePerpAggregatedPositions = (
  aevo: IAggregatedPosition[],
  binance: IAggregatedPosition[],
  bybit: IAggregatedPosition[],
  hyperliquid: IAggregatedPosition[],
  lighter: IAggregatedPosition[],
  marketsData?: IMarketLike[],
  lighterMainnet: IAggregatedPosition[] = []
): IPerpAggregatedPosition[] => {
  const positionMap: Record<string, IPerpAggregatedPosition> = {};

  const ensurePosition = (instrumentName: string, asset: string, markPrice: number) => {
    if (!positionMap[instrumentName]) {
      positionMap[instrumentName] = {
        instrument_name: instrumentName,
        asset,
        contracts: 0,
        pnl: 0,
        side: "-",
        aevoDelta: 0,
        binanceDelta: 0,
        bybitDelta: 0,
        hyperliquidDelta: 0,
        lighterDelta: 0,
        lighterMainnetDelta: 0,
        netDelta: 0,
        aevoMarkPrice: 0,
        binanceMarkPrice: 0,
        bybitMarkPrice: 0,
        hyperliquidMarkPrice: 0,
        lighterMarkPrice: 0,
        lighterMainnetMarkPrice: 0,
        markPrice,
      };
    }
  };

  aevo.forEach((pos) => {
    const asset = retrieveAssetByInstrumentName(pos.instrument_name);
    const markPrice = Number(marketsData?.find((m) => m.underlying_asset === asset)?.mark_price ?? 0);
    ensurePosition(pos.instrument_name, pos.asset, markPrice);
    positionMap[pos.instrument_name].aevoDelta = pos.contracts;
    positionMap[pos.instrument_name].aevoMarkPrice = markPrice;
    positionMap[pos.instrument_name].contracts += pos.contracts;
    positionMap[pos.instrument_name].pnl += pos.pnl || 0;
  });

  binance.forEach((pos) => {
    const asset = retrieveAssetByInstrumentName(pos.instrument_name);
    const markPrice = Number(marketsData?.find((m) => m.underlying_asset === asset)?.mark_price ?? 0);
    ensurePosition(pos.instrument_name, pos.asset, markPrice);
    positionMap[pos.instrument_name].binanceDelta = pos.contracts;
    positionMap[pos.instrument_name].binanceMarkPrice = markPrice;
    positionMap[pos.instrument_name].contracts += pos.contracts;
    positionMap[pos.instrument_name].pnl += pos.pnl || 0;
  });

  bybit.forEach((pos) => {
    const asset = retrieveAssetByInstrumentName(pos.instrument_name);
    const markPrice = Number(marketsData?.find((m) => m.underlying_asset === asset)?.mark_price ?? 0);
    ensurePosition(pos.instrument_name, pos.asset, markPrice);
    positionMap[pos.instrument_name].bybitDelta = pos.contracts;
    positionMap[pos.instrument_name].bybitMarkPrice = markPrice;
    positionMap[pos.instrument_name].contracts += pos.contracts;
    positionMap[pos.instrument_name].pnl += pos.pnl || 0;
  });

  hyperliquid.forEach((pos) => {
    const asset = retrieveAssetByInstrumentName(pos.instrument_name);
    const markPrice = Number(marketsData?.find((m) => m.underlying_asset === asset)?.mark_price ?? 0);
    ensurePosition(pos.instrument_name, pos.asset, markPrice);
    positionMap[pos.instrument_name].hyperliquidDelta = pos.contracts;
    positionMap[pos.instrument_name].hyperliquidMarkPrice = pos.hyperliquidMarkPrice || markPrice;
    positionMap[pos.instrument_name].contracts += pos.contracts;
    positionMap[pos.instrument_name].pnl += pos.pnl || 0;
  });

  lighter.forEach((pos) => {
    const asset = retrieveAssetByInstrumentName(pos.instrument_name);
    const markPrice = Number(
      marketsData?.find((m) => m.underlying_asset === asset)?.mark_price ??
        pos.lighterMarkPrice ??
        0
    );
    ensurePosition(pos.instrument_name, pos.asset, markPrice);
    if (!positionMap[pos.instrument_name].markPrice && pos.lighterMarkPrice) {
      positionMap[pos.instrument_name].markPrice = pos.lighterMarkPrice;
    }
    positionMap[pos.instrument_name].lighterDelta = pos.contracts;
    positionMap[pos.instrument_name].lighterMarkPrice = pos.lighterMarkPrice || markPrice;
    positionMap[pos.instrument_name].contracts += pos.contracts;
    positionMap[pos.instrument_name].pnl += pos.pnl || 0;
  });

  lighterMainnet.forEach((pos) => {
    const asset = retrieveAssetByInstrumentName(pos.instrument_name);
    const markPrice = Number(
      marketsData?.find((m) => m.underlying_asset === asset)?.mark_price ??
        pos.lighterMainnetMarkPrice ??
        0
    );
    ensurePosition(pos.instrument_name, pos.asset, markPrice);
    if (!positionMap[pos.instrument_name].markPrice && pos.lighterMainnetMarkPrice) {
      positionMap[pos.instrument_name].markPrice = pos.lighterMainnetMarkPrice;
    }
    positionMap[pos.instrument_name].lighterMainnetDelta = pos.contracts;
    positionMap[pos.instrument_name].lighterMainnetMarkPrice = pos.lighterMainnetMarkPrice || markPrice;
    positionMap[pos.instrument_name].contracts += pos.contracts;
    positionMap[pos.instrument_name].pnl += pos.pnl || 0;
  });

  const positions = Object.values(positionMap).map((pos) => {
    const netDelta =
      pos.aevoDelta +
      pos.binanceDelta +
      pos.bybitDelta +
      pos.hyperliquidDelta +
      pos.lighterDelta +
      pos.lighterMainnetDelta;
    let side = "-";
    if (pos.contracts > 0) side = "buy";
    else if (pos.contracts < 0) side = "sell";
    return { ...pos, netDelta, side };
  });

  return sortPerpPositions(positions);
};
